import { useState } from "react";
import { Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function DeleteAccountSection() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const close = () => {
    if (busy) return;
    setOpen(false);
    setEmail("");
    setPassword("");
    setConfirmation("");
    setError("");
  };

  const remove = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/users/me/delete-account", { email, password, confirmation });
      await logout();
      navigate("/", { replace: true });
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Kontoen kunne ikke slettes.");
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-[#E7C9C5] bg-white p-5 sm:p-6" data-testid="delete-account-section">
      <h2 className="text-base font-semibold text-[#7F1D1D]">Slet profil og konto</h2>
      <p className="mt-2 max-w-2xl text-sm leading-6 text-[#6B5B58]">
        Din adgang lukkes med det samme. En ubrugt konto slettes helt. Nødvendig historik fra gennemførte aftaler kan blive bevaret uden din offentlige profil.
      </p>
      <button type="button" onClick={() => setOpen(true)} className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-lg border border-[#B91C1C] px-4 text-sm font-semibold text-[#991B1B] hover:bg-[#FEF2F2]">
        <Trash2 size={16}/> Slet min konto
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-4" role="presentation">
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl sm:p-7" role="dialog" aria-modal="true" aria-labelledby="delete-account-title">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="delete-account-title" className="text-xl font-semibold text-brand-ink">Slet din konto permanent?</h2>
                <p className="mt-2 text-sm leading-6 text-brand-body">Handlingen kan ikke fortrydes. Aktive aftaler skal afsluttes eller aflyses først.</p>
              </div>
              <button type="button" onClick={close} aria-label="Luk" className="rounded-full p-2 text-brand-muted hover:bg-brand-soft"><X size={18}/></button>
            </div>
            <form onSubmit={remove} className="mt-5 space-y-4">
              <label className="block text-sm font-medium text-brand-ink">
                Din e-mail
                <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder={user?.email || ""} required autoComplete="email" className="mt-1.5 w-full rounded-lg border border-brand-line px-3 py-2.5"/>
              </label>
              <label className="block text-sm font-medium text-brand-ink">
                Din adgangskode
                <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required autoComplete="current-password" className="mt-1.5 w-full rounded-lg border border-brand-line px-3 py-2.5"/>
              </label>
              <label className="block text-sm font-medium text-brand-ink">
                Skriv <strong>SLET MIN KONTO</strong>
                <input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required autoComplete="off" className="mt-1.5 w-full rounded-lg border border-brand-line px-3 py-2.5"/>
              </label>
              {error && <p className="text-sm text-[#991B1B]" role="alert">{error}</p>}
              <div className="flex flex-wrap justify-end gap-3 pt-2">
                <button type="button" onClick={close} disabled={busy} className="min-h-11 rounded-lg border border-brand-line px-4 text-sm font-semibold text-brand-ink">Behold min konto</button>
                <button type="submit" disabled={busy || confirmation.trim().toUpperCase() !== "SLET MIN KONTO"} className="min-h-11 rounded-lg bg-[#B91C1C] px-4 text-sm font-semibold text-white disabled:opacity-40">
                  {busy ? "Sletter…" : "Slet permanent"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

